import React, { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import { db } from "../firebase";
import { ref, onValue } from "firebase/database";
import {
  Server,
  AlertTriangle,
  CheckCircle,
  Wrench,
  Clock,
} from "lucide-react";

const SENSOR_KEYS = [
  "suhu",
  "kelembapan",
  "co2",
  "co",
  "kebisingan",
  "cahaya",
];

const DEVICE_TIMEOUT = 10 * 60 * 1000; // 10 menit

export default function HistoriTeknis() {
  const [deviceMap, setDeviceMap] = useState({});
  const [kelasData, setKelasData] = useState({});
  const [deviceStatus, setDeviceStatus] = useState({});
  const [historis, setHistoris] = useState({});
  const [ambangBatas, setAmbangBatas] = useState({});
  const [activeCount, setActiveCount] = useState(0);
  const [troubleCount, setTroubleCount] = useState(0);
  const [filterAbnormal, setFilterAbnormal] = useState([]);
  const [errorSensor, setErrorSensor] = useState([]);

  useEffect(() => {
    const mapRef = ref(db, "deviceMapping");
    const kelasRef = ref(db, "kelas");
    const historiRef = ref(db, "dataHistoris");
    const ambangRef = ref(db, "ambangBatas");
    const statusRef = ref(db, "deviceStatus");

    onValue(mapRef, (snap) => {
      setDeviceMap(snap.val() || {});
    });

    onValue(kelasRef, (snap) => {
      setKelasData(snap.val() || {});
    });

    onValue(historiRef, (snap) => {
      setHistoris(snap.val() || {});
    });

    onValue(ambangRef, (snap) => {
      setAmbangBatas(snap.val() || {});
    });

    onValue(statusRef, (snap) => {
      setDeviceStatus(snap.val() || {});
    });
  }, []);

  // Hitung activeCount dan troubleCount dengan benar
  useEffect(() => {
    let active = 0;
    let trouble = 0;
    
    Object.keys(deviceMap).forEach((deviceId) => {
      if (getDeviceStatus(deviceId) === "active") {
        active++;
      } else {
        trouble++;
      }
    });
    
    setActiveCount(active);
    setTroubleCount(trouble);
  }, [deviceMap, kelasData]);

  // Riwayat Abnormal
  useEffect(() => {
    // Tunggu sampai kedua data sudah ada
    if (!historis || Object.keys(historis).length === 0 || 
      !ambangBatas || Object.keys(ambangBatas).length === 0) {
      setFilterAbnormal([]);
      return;
    }

    const abnormalData = [];
    const duaMingguLalu = Date.now() - (14 * 24 * 60 * 60 * 1000);

    Object.values(historis).forEach((tanggal) => {
      if (!tanggal) return;

      Object.values(tanggal).forEach((item) => {
        if (!item || !item.timestamp) return;

        const timestamp = new Date(item.timestamp).getTime();
        if (isNaN(timestamp) || timestamp < duaMingguLalu) return;

        // Loop sensor yang ada di ambangBatas
        Object.keys(ambangBatas).forEach((sensor) => {
          const value = item[sensor];
          if (value == null || value === "") return;

          const rule = ambangBatas[sensor];
          if (!rule) return;

          Object.entries(rule).forEach(([kategori, range]) => {
            if (kategori.toLowerCase().includes("bahaya")) return;
            let adaAbnormal = false;

            if (kategori.toLowerCase().includes("batas atas")) {
              // bahaya batas atas
              if (value >= range.max) {adaAbnormal = true;}
            }
            else if (kategori.toLowerCase().includes("batas bawah")) {
              // bahaya batas bawah
              if (value <= range.min) {adaAbnormal = true;}
            }
            else {
              // bahaya
              if (value >= range.max) {adaAbnormal = true;}
            }

            if (adaAbnormal) {
              abnormalData.push({
                waktu: item.timestamp,
                timestamp: timestamp,
                kelas: item.kelas,
                sensor,
                nilai: Number(value).toFixed(1),
                status: "Bahaya",
                error: "Tidak ada data abnormal"
              });
            }
          });
        });
      });
    });

    // Ambil hanya yang TERBARU per kelas + sensor
    const abnormalTerbaruMap = new Map();
    abnormalData.forEach(item => {
      const key = `${item.kelas}-${item.sensor}`;
      if (!abnormalTerbaruMap.has(key) || item.timestamp > abnormalTerbaruMap.get(key).timestamp) {
        abnormalTerbaruMap.set(key, item);
      }
    });

    // sorting waktu terbaru
    const sortAbnormal = Array.from(abnormalTerbaruMap.values()).sort((a, b) => b.timestamp - a.timestamp);
    setFilterAbnormal(sortAbnormal);
  }, [historis, ambangBatas]);

  useEffect(() => {
    const error = [];
    const sebulan = Date.now() - (30 * 24 * 60 * 60 * 1000);

    // Sensor trouble
    Object.entries(deviceStatus).forEach(([deviceId, data]) => {
      if (data.status==="trouble") {
        Object.entries(data.sensorStatus || {}).forEach(([sensor, status]) => {
          if (status==="trouble") {
            const timestamp = new Date(data.lastSeen).getTime();

            // filter hanya dlm 30 hari terakhir
            if (timestamp >= sebulan) {
              error.push({
                waktu: data.lastSeen,
                timestamp: timestamp,
                kelas: data.kelas,
                deviceId: deviceId,
                sensor,
                error: "Tidak ada data masuk",
              });
            }
          }
        });
      }
    });

    // sorting waktu terbaru
    const sortError = error.sort((a, b) => b.timestamp - a.timestamp);
    setErrorSensor(sortError);
  }, [deviceStatus]);

  const getDeviceStatus = (deviceId) => {
    const data = deviceStatus[deviceId];

    if (!data?.lastSeen) return "trouble";

    return data.status === "active"
      ? "active"
      : "trouble";
  };

  const getSensorStatus = (deviceId, sensor) => {
    const data = deviceStatus[deviceId];

    if (!data?.lastSeenSensor) {
      return "trouble";
    }

    return data.sensorStatus[sensor] === "active"
      ? "active"
      : "trouble";
  };

  const totalDevice = Object.keys(deviceMap).length;

  return (
    <>
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-8">
        {/* RINGKASAN SISTEM */}
        <div>
          <h2 className="text-2xl font-bold mb-4">
            Ringkasan Sistem
          </h2>

          <div className="grid grid-cols-3 gap-2 sm:gap-3 md:gap-4">
            <div className="bg-white shadow rounded-xl p-3 sm:p-4 md:p-5 border">
              <Server className="mb-2 text-blue-500 w-5 h-5 sm:w-6 sm:h-6" />
              <h3 className="text-m sm:text-sm text-gray-500">
                Total Device
              </h3>
              <p className="text-xl font-bold">
                {totalDevice}
              </p>
            </div>

            <div className="bg-green-50 shadow rounded-xl p-3 sm:p-4 md:p-5 border border-green-300">
              <CheckCircle className="mb-2 text-green-600 w-5 h-5 sm:w-6 sm:h-6" />
              <h3 className="text-m sm:text-sm text-green-700">
                Device Aktif
              </h3>
              <p className="text-xl font-bold text-green-700">
                {
                  Object.values(deviceMap).filter(
                    (deviceId) =>
                      getDeviceStatus(deviceId) === "active"
                  ).length
                }
              </p>
            </div>

            <div className="bg-red-50 shadow rounded-xl p-3 sm:p-4 md:p-5 border border-red-300">
              <AlertTriangle className="mb-2 text-red-600 w-5 h-5 sm:w-6 sm:h-6" />
              <h3 className="text-m text-red-700">
                Device Trouble
              </h3>
              <p className="text-xl font-bold text-red-700">
                {
                  Object.values(deviceMap).filter(
                    (deviceId) =>
                      getDeviceStatus(deviceId) === "trouble"
                  ).length
                }
              </p>
            </div>
          </div>
        </div>

        {/* STATUS DEVICE */}
        <div className="bg-white rounded-xl shadow border p-5">
          <h2 className="text-xl font-bold mb-4">
            Status Device
          </h2>

          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-100 items-center">
                <tr>
                  <th className="p-3">Last Seen</th>
                  <th className="p-3">Kelas</th>
                  <th className="p-3">Device ID</th>
                  <th className="p-3">Status Device</th>
                  <th className="p-3">SHT31</th>
                  <th className="p-3">MQ-135</th>
                  <th className="p-3">TSL2561</th>
                  <th className="p-3">KY-038</th>
                </tr>
              </thead>

              <tbody>
                {Object.entries(deviceMap)
                  .sort(([, a], [, b]) => {
                    const parseKelas = (nama) => {
                      const match = nama.match(/Kelas\s*(\d+)([A-Z]?)/i);

                      return {
                        nomor: match ? parseInt(match[1], 10) : 999,
                        huruf: match ? (match[2] || "") : "",
                      };
                    };

                    const kelasA = parseKelas(a);
                    const kelasB = parseKelas(b);

                    // urut nomor kelas
                    if (kelasA.nomor !== kelasB.nomor) {
                      return kelasA.nomor - kelasB.nomor;
                    }
                    // urut huruf
                    return kelasA.huruf.localeCompare(kelasB.huruf);
                  })
                  .map(([deviceId, kelas]) => {
                      const statusData = deviceStatus[deviceId];
                      const status = getDeviceStatus(kelas);

                      return (
                        <tr
                          key={deviceId}
                          className="border-t"
                        >
                          <td className="p-3">
                            {statusData?.lastSeen? new Date(statusData.lastSeen).toLocaleString("id-ID")
                              : "-"}
                          </td>

                          <td className="p-3">
                            {kelas}
                          </td>

                          <td className="p-3">
                            {deviceId}
                          </td>

                          <td className="p-3">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                                status === "active"
                                  ? "bg-green-100 text-green-700"
                                  : "bg-red-100 text-red-700"
                              }`}
                            >
                              {status}
                            </span>
                          </td>

                          <td className="p-3 text-center">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${
                                getSensorStatus(deviceId, "suhu") === "active" &&
                                getSensorStatus(deviceId, "kelembapan") === "active"
                                  ? "bg-green-100 text-green-700"
                                  : "bg-red-100 text-red-700"
                              }`}
                            >
                              {getSensorStatus(deviceId, "suhu") === "active" &&
                              getSensorStatus(deviceId, "kelembapan") === "active"
                                ? "Active"
                                : "Trouble"}
                            </span>
                          </td>

                          <td className="p-3 text-center">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${
                                getSensorStatus(deviceId, "co") === "active" &&
                                getSensorStatus(deviceId, "co2") === "active"
                                  ? "bg-green-100 text-green-700"
                                  : "bg-red-100 text-red-700"
                              }`}
                            >
                              {getSensorStatus(deviceId, "co") === "active" &&
                              getSensorStatus(deviceId, "co2") === "active"
                                ? "Active"
                                : "Trouble"}
                            </span>
                          </td>

                          <td className="p-3 text-center">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${
                                getSensorStatus(deviceId, "cahaya") === "active"
                                  ? "bg-green-100 text-green-700"
                                  : "bg-red-100 text-red-700"
                              }`}
                            >
                              {getSensorStatus(deviceId, "cahaya") === "active"
                                ? "Active"
                                : "Trouble"}
                            </span>
                          </td>

                          <td className="p-3 text-center">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${
                                getSensorStatus(deviceId, "kebisingan") === "active"
                                  ? "bg-green-100 text-green-700"
                                  : "bg-red-100 text-red-700"
                              }`}
                            >
                              {getSensorStatus(deviceId, "kebisingan") === "active"
                                ? "Active"
                                : "Trouble"}
                            </span>
                          </td>
                        </tr>
                      );
                    }
                  )}
              </tbody>
            </table>
          </div>
        </div>

        {/* PARAMETER ABNORMAL */}
        <div className="bg-white rounded-xl shadow border p-5">
          <h2 className="text-xl font-bold mb-4">
            Riwayat Data Parameter Abnormal Terbaru
          </h2>

          <div className="overflow-auto max-h-96">
            <table className="w-full text-sm">
              <thead className="bg-yellow-50 items-center">
                <tr>
                  <th className="p-3">Waktu</th>
                  <th className="p-3">Kelas</th>
                  <th className="p-3">Parameter</th>
                  <th className="p-3">Nilai</th>
                  <th className="p-3">Status</th>
                </tr>
              </thead>

              <tbody>
                {filterAbnormal.length > 0 ? (
                  filterAbnormal.map((item, index) => (
                    <tr key={index} className="border-t">
                      <td className="p-3">
                        {new Date(item.waktu).toLocaleString('id-ID')}
                      </td>

                      <td className="p-3">
                        {item.kelas}
                      </td>

                      <td className="p-3">
                        {item.sensor}
                      </td>

                      <td className="p-3">
                        {item.nilai}
                      </td>

                      <td className="p-3 capitalize">
                        {item.status}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center py-10 text-gray-500">
                      Tidak ada riwayat nilai abnormal dalam 2 minggu terakhir
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ERROR SENSOR */}
        <div className="bg-white rounded-xl shadow border p-5">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">Riwayat Error Sensor</h2>
            <span className="text-sm text-gray-500">
              {errorSensor.length} error aktif
            </span>
          </div>

          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-red-50 items-center">
                <tr>
                  <th className="p-3">Waktu</th>
                  <th className="p-3">Kelas</th>
                  <th className="p-3">Id Device</th>
                  <th className="p-3">Sensor</th>
                  <th className="p-3">Jenis Error</th>
                </tr>
              </thead>

              <tbody>
                {errorSensor.length > 0 ? (
                  errorSensor.map((item, index) => (
                    <tr
                      key={index}
                      className="border-t"
                    >
                      <td className="p-3">
                        {new Date(item.waktu).toLocaleString('id-ID')}
                      </td>

                      <td className="p-3">
                        {item.kelas}
                      </td>

                      <td className="p-3">
                        {item.sensor}
                      </td>

                      <td className="p-3 text-red-600 font-medium">
                        {item.error}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="4"
                      className="text-center py-8 text-gray-400"
                    >
                      Tidak ada error sensor dalam 30 hari terakhir.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}